import { Input, Select, TextArea } from '../../../../shared/components/FormControls';
import Rodal from 'rodal';
import { Button } from '../../../../shared/components/Button';
import { compareImageFileNames, resizeImage } from '../../utils/imageUtils';
import '../../../../shared/styles/Orchestrator.css';
import 'rodal/lib/rodal.css';
import { Image, ImagePlus, Pencil, Type, Wallpaper } from 'lucide-react';
import { useEffect, useState } from 'react';
import { hexGridAspectRatio } from '../../utils/gridConfig';

const AVAILABLE_FONTS = [
  { label: 'Sans-Serif', value: 'sans-serif' },
  { label: 'Serif', value: 'serif' },
  { label: 'Monospace', value: 'monospace' },
  { label: 'Cursive', value: 'cursive' },
  { label: 'Fantasy', value: 'fantasy' },
  { label: 'Georgia', value: 'Georgia, serif' },
  { label: 'Verdana', value: 'Verdana, sans-serif' },
  { label: 'Impact', value: 'Impact, fantasy' },
  { label: 'Arial Black', value: '"Arial Black", sans-serif' },
  { label: 'Courier New', value: '"Courier New", monospace' },
];

function LayerModal({
  visible,
  form,
  isEditing,
  onChange,
  onCancel,
  onSave,
  onDelete,
  onMassAdd,
  mode = 'light',
  gridConfig = { cols: 24, rows: 30, type: 'square' },
}) {
  const isDark = mode === 'dark';
  const [isPhone, setIsPhone] = useState(false);
  const [massAddMode, setMassAddMode] = useState(false);
  const [massGroup, setMassGroup] = useState('');
  const [massFiles, setMassFiles] = useState([]);
  const [massError, setMassError] = useState('');
  const [isMassUploading, setIsMassUploading] = useState(false);
  const gridCols = gridConfig.cols || 24;
  const gridRows = gridConfig.rows || 30;
  const gridAspectRatio = gridConfig.type === 'hex'
    ? hexGridAspectRatio(gridCols, gridRows)
    : gridCols / gridRows;
  const previewHeight = 180;

  useEffect(() => {
    const mediaQueryWidth = window.matchMedia('(max-width: 620px)');
    const handleMediaChange = () => {
      setIsPhone(mediaQueryWidth.matches);
    };
    handleMediaChange();
    mediaQueryWidth.addListener(handleMediaChange);
    return () => {
      mediaQueryWidth.removeListener(handleMediaChange);
    };
  }, []);

  const triggerFilePicker = () => {
    document.getElementById('layer_img_input')?.click();
  };

  const handleMassAdd = async () => {
    if (!massGroup.trim() || massFiles.length === 0 || isMassUploading) return;
    setIsMassUploading(true);
    setMassError('');
    try {
      await onMassAdd(massFiles, massGroup.trim());
    } catch (error) {
      console.error('[LayerModal] Bulk image add failed:', error);
      setMassError('Some images could not be added. Please try again.');
    } finally {
      setIsMassUploading(false);
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const resized = await resizeImage(reader.result, 2560, Infinity);
        onChange({ file, preview: resized });
      } catch (err) {
        console.error('[LayerModal] Image resize failed:', err);
      }
    };
    reader.readAsDataURL(file);
    event.target.value = '';
  };

  const layerType = form.type || 'image';
  const TYPE_LABELS = { image: 'Image', text: 'Text', draw: 'Draw' };

  const modalHeight = isPhone
    ? 'calc(100% - 114px)'
    : massAddMode
    ? 390
    : layerType === 'text'
    ? 758
    : layerType === 'draw'
    ? 480
    : 710;

  return (
    <Rodal
      animation='slideUp'
      visible={visible}
      onClose={onCancel}
      className={isDark ? 'df_rodal_dark' : ''}
      customStyles={{
        border: isDark ? '2px solid var(--onyx)' : '2px solid #e4E7C5',
        backgroundColor: isDark ? 'var(--jet)' : '#f6f7eb',
        color: isDark ? 'var(--ivory)' : 'var(--jet)',
        width: isPhone ? 'calc(100% - 34px)' : 420,
        height: isPhone ? 'calc(100% - 114px)' : modalHeight,
        marginTop: isPhone ? '80px' : '',
      }}
    >
      <div
        className={`trialment_login_form ${isDark ? 'df_modal_form_dark' : ''}`}
        style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
      >
        <div style={{ flex: 1, overflowY: isPhone ? 'auto' : 'visible' }}>
          {/* Header */}
          <div
            className='trialment_login_form_row'
            style={{ fontWeight: 700, fontSize: 16, marginBottom: 6, textAlign: 'center' }}
          >
            {isEditing
              ? `Edit Layer \u2013 ${TYPE_LABELS[layerType] || 'Image'}`
              : 'Add Layer'}
          </div>

          {/* Type selector – only when creating */}
          {!isEditing && (
            <div
              className='trialment_login_form_row'
              style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}
            >
              <Button
                label='Image'
                icon={Image}
                type={layerType === 'image' ? 'tomato' : 'black-white'}
                onPress={() => {
                  setMassAddMode(false);
                  onChange({ type: 'image' });
                }}
              />
              <Button
                label='Text'
                icon={Type}
                type={layerType === 'text' ? 'tomato' : 'black-white'}
                onPress={() => {
                  setMassAddMode(false);
                  onChange({ type: 'text' });
                }}
              />
              <Button
                label='Draw'
                icon={Pencil}
                type={layerType === 'draw' ? 'tomato' : 'black-white'}
                onPress={() => {
                  setMassAddMode(false);
                  onChange({ type: 'draw' });
                }}
              />
              <Button
                label='Mass Add Images'
                icon={ImagePlus}
                type={massAddMode ? 'tomato' : 'black-white'}
                onPress={() => setMassAddMode(true)}
              />
            </div>
          )}

          {/* Common fields */}
          {!massAddMode && <div className='trialment_login_form_row'>
            <Input
              type='text'
              placeholder='Name'
              value={form.name}
              onChange={(event) => onChange({ name: event.target.value })}
            />
          </div>}
          {!massAddMode && <div className='trialment_login_form_row'>
            <Input
              type='text'
              placeholder='Group'
              value={form.group || ''}
              onChange={(event) => onChange({ group: event.target.value })}
            />
          </div>}

          {massAddMode && (
            <>
              <div className='trialment_login_form_row'>
                <Input
                  type='text'
                  placeholder='Group name'
                  value={massGroup}
                  onChange={(event) => setMassGroup(event.target.value)}
                  disabled={isMassUploading}
                />
              </div>
              <div
                className='trialment_login_form_row'
                style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
              >
                <Button
                  label='Choose Images'
                  icon={ImagePlus}
                  type='black-white'
                  disabled={isMassUploading}
                  onPress={() => document.getElementById('layer_mass_img_input')?.click()}
                />
                <Input
                  id='layer_mass_img_input'
                  type='file'
                  accept='.png,.gif,.jpg,.jpeg,.webp'
                  multiple
                  style={{ display: 'none' }}
                  onChange={(event) => {
                    setMassFiles(Array.from(event.target.files || []));
                    setMassError('');
                    event.target.value = '';
                  }}
                />
                <span style={{ fontSize: '12px', opacity: 0.75 }}>
                  {massFiles.length} image{massFiles.length === 1 ? '' : 's'} selected
                </span>
              </div>
              {massFiles.length > 0 && (
                <div
                  className='trialment_login_form_row'
                  style={{ maxHeight: 130, overflowY: 'auto', fontSize: 12 }}
                >
                  {massFiles
                    .slice()
                    .sort((a, b) => compareImageFileNames(a.name, b.name))
                    .map((file) => <div key={`${file.name}-${file.lastModified}`}>{file.name}</div>)}
                </div>
              )}
              {massError && (
                <div className='trialment_login_form_row' role='alert' style={{ color: '#c0392b', fontSize: 12 }}>
                  {massError}
                </div>
              )}
            </>
          )}

          {/* ─── IMAGE LAYER ─── */}
          {!massAddMode && layerType === 'image' && (
            <>
              <div
                className='trialment_login_form_row'
                style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
              >
                <Button
                  label='Add Image'
                  icon={ImagePlus}
                  type='black-white'
                  onPress={triggerFilePicker}
                />
                <Input
                  id='layer_img_input'
                  type='file'
                  accept='.png,.gif,.jpg,.jpeg'
                  style={{ display: 'none' }}
                  onChange={handleFileChange}
                />
              </div>
              <div
                className='trialment_login_form_row'
                style={{
                  height: `${previewHeight}px`,
                  width: `${previewHeight * gridAspectRatio}px`,
                  border: isDark ? '1px solid #4a4f52' : '1px solid #ccc',
                  background: isDark ? 'var(--onyx)' : '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginTop: '10px',
                  paddingTop: '0px',
                }}
              >
                <div
                  style={{
                    height: '100%',
                    width: '100%',
                  }}
                >
                  {form.preview ? (
                    <img
                      alt=''
                      src={form.preview}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                      }}
                    />
                  ) : (
                    <span
                    style={{
                      height: '100%',
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      textAlign: 'center',
                      color: isDark ? 'rgba(246, 247, 235, 0.75)' : undefined,
                    }}
                    >
                      {gridCols}w x {gridRows}h
                    </span>
                  )}
                </div>
              </div>
              <div
                className='trialment_login_form_row'
                style={{
                  display: 'flex',
                  gap: '10px',
                  columnGap: '10px',
                  rowGap: '10px',
                  flexWrap: 'wrap',
                  alignItems: 'flex-start',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px',
                    flex: '1 1 150px',
                    minWidth: '140px',
                    maxWidth: '150px',
                    width: '100%',
                  }}
                >
                  <label style={{ fontSize: '12px', fontWeight: 600 }}>
                    Hierarchy (z)
                  </label>
                  <Input
                    type='number'
                    value={form.z}
                    onChange={(event) => onChange({ z: event.target.value })}
                    style={{ width: '100%' }}
                  />
                </div>
                <div style={{ width: '12px' }}></div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px',
                    flex: '1 1 160px',
                    minWidth: '140px',
                  }}
                >
                  <label style={{ fontSize: '12px', fontWeight: 600 }}>
                    Background
                  </label>
                  <Input
                    type='text'
                    placeholder='#RRGGBB or empty'
                    value={form.bgColor}
                    onChange={(event) =>
                      onChange({ bgColor: event.target.value })
                    }
                    style={{ width: '100%' }}
                  />
                </div>
              </div>
              <div
                className='trialment_login_form_row'
                style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
              >
                <Button
                  icon={Wallpaper}
                  label='Center Screen when Shown'
                  type={form.centerOnShow ? 'tomato' : 'black-white'}
                  onPress={() =>
                    onChange({ centerOnShow: !form.centerOnShow })
                  }
                />
              </div>
            </>
          )}

          {/* ─── TEXT LAYER ─── */}
          {!massAddMode && layerType === 'text' && (
            <>
              <div className='trialment_login_form_row'>
                <TextArea
                  placeholder='Enter text...'
                  value={form.text || ''}
                  rows={4}
                  onChange={(e) => onChange({ text: e.target.value })}
                  style={{
                    width: '100%',
                    resize: 'none',
                    minHeight: '45px',
                    minWidth: '350px',
                    boxSizing: 'border-box',
                  }}
                />
              </div>
              <div
                className='trialment_login_form_row'
                style={{
                  display: 'flex',
                  gap: '10px',
                  flexWrap: 'nowrap',
                  alignItems: 'flex-start',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px',
                    flex: '1 1 0',
                    minWidth: '0',
                  }}
                >
                  <label style={{ fontSize: '12px', fontWeight: 600 }}>
                    Font
                  </label>
                  <Select
                    value={form.font || 'sans-serif'}
                    onChange={(e) => onChange({ font: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '6px 8px',
                      background: isDark ? 'var(--onyx)' : '#fff',
                      color: isDark ? 'var(--ivory)' : 'var(--jet)',
                      border: isDark
                        ? '1px solid var(--ivory)'
                        : '1px solid #ccc',
                      borderRadius: '4px',
                      fontSize: '14px',
                    }}
                  >
                    {AVAILABLE_FONTS.map((f) => (
                      <option
                        key={f.value}
                        value={f.value}
                        style={{ fontFamily: f.value }}
                      >
                        {f.label}
                      </option>
                    ))}
                  </Select>
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px',
                    flex: '0 0 120px',
                    minWidth: '120px',
                  }}
                >
                  <label style={{ fontSize: '12px', fontWeight: 600 }}>
                    Font Size (px)
                  </label>
                  <Input
                    type='number'
                    min={8}
                    max={200}
                    value={form.fontSize || 36}
                    onChange={(e) => onChange({ fontSize: e.target.value })}
                    style={{ width: '100%' }}
                  />
                </div>
              </div>
              <div
                className='trialment_login_form_row'
                style={{
                  display: 'flex',
                  gap: '10px',
                  flexWrap: 'nowrap',
                  alignItems: 'flex-start',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px',
                    flex: '1 1 0',
                    minWidth: '0',
                  }}
                >
                  <label style={{ fontSize: '12px', fontWeight: 600 }}>
                    Text Color
                  </label>
                  <div
                    style={{ display: 'flex', gap: '6px', alignItems: 'center' }}
                  >
                    <Input
                      type='color'
                      value={form.color || '#ffffff'}
                      onChange={(e) => onChange({ color: e.target.value })}
                    />
                    <Input
                      type='text'
                      value={form.color || '#ffffff'}
                      onChange={(e) => onChange({ color: e.target.value })}
                      style={{ flex: 1, width: '150px' }}
                      placeholder='#ffffff'
                    />
                  </div>
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px',
                    flex: '0 0 120px',
                    minWidth: '120px',
                  }}
                >
                  <label style={{ fontSize: '12px', fontWeight: 600 }}>
                    Hierarchy (z)
                  </label>
                  <Input
                    type='number'
                    value={form.z}
                    onChange={(event) => onChange({ z: event.target.value })}
                    style={{ width: '100%' }}
                  />
                </div>
              </div>
              {/* Live preview – font size capped at 60px in the modal to fit the preview box;
                   the text will appear at the full configured size on the board */}
              <div
                className='trialment_login_form_row'
                style={{
                  padding: '8px',
                  background: isDark ? 'var(--onyx)' : '#fff',
                  border: isDark ? '1px solid #4a4f52' : '1px solid #ccc',
                  borderRadius: '4px',
                  fontFamily: form.font || 'sans-serif',
                  fontSize: `${Math.min(Number(form.fontSize) || 36, 60)}px`,
                  color: form.color || '#ffffff',
                  minHeight: '80px',
                  maxHeight: '32px',
                  width: '350px',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  textShadow: '0 1px 3px rgba(0,0,0,0.5)',
                  marginTop: '14px'
                }}
              >
                {form.text || (
                  <span style={{ opacity: 0.4 }}>Preview</span>
                )}
              </div>
              <div
                className='trialment_login_form_row'
                style={{ fontSize: '11px', opacity: 0.7, marginTop: '4px' }}
              >
                Preview capped at 60 px; text renders at {form.fontSize || 36} px on the board. Drag to reposition.
              </div>
            </>
          )}

          {/* ─── DRAW LAYER ─── */}
          {!massAddMode && layerType === 'draw' && (
            <>
              <div
                className='trialment_login_form_row'
                style={{
                  display: 'flex',
                  gap: '10px',
                  flexWrap: 'wrap',
                  alignItems: 'flex-start',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px',
                    flex: '1 1 120px',
                    minWidth: '110px',
                  }}
                >
                  <label style={{ fontSize: '12px', fontWeight: 600 }}>
                    Draw Color
                  </label>
                  <div
                    style={{ display: 'flex', gap: '6px', alignItems: 'center' }}
                  >
                    <Input
                      type='color'
                      value={form.drawColor || '#e94f37'}
                      onChange={(e) => onChange({ drawColor: e.target.value })}
                    />
                    <Input
                      type='text'
                      value={form.drawColor || '#e94f37'}
                      onChange={(e) => onChange({ drawColor: e.target.value })}
                      style={{ flex: 1 , width: '150px' }}
                      placeholder='#e94f37'
                    />
                  </div>
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px',
                    minWidth: '110px',
                  }}
                >
                  <label style={{ fontSize: '12px', fontWeight: 600 }}>
                    Stroke Width (px)
                  </label>
                  <Input
                    type='number'
                    min={1}
                    max={40}
                    value={form.strokeWidth || 4}
                    onChange={(e) => onChange({ strokeWidth: e.target.value })}
                    style={{ width: '100%' }}
                  />
                </div>
              </div>
              <div
                className='trialment_login_form_row'
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '6px',
                }}
              >
                <label style={{ fontSize: '12px', fontWeight: 600 }}>
                  Hierarchy (z)
                </label>
                <Input
                  type='number'
                  value={form.z}
                  onChange={(event) => onChange({ z: event.target.value })}
                  style={{ width: '100%' }}
                />
              </div>
              {isEditing &&
                Array.isArray(form.paths) &&
                form.paths.length > 0 && (
                  <div
                    className='trialment_login_form_row'
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                    }}
                  >
                    <Button
                      icon={Pencil}
                      label={`Clear Drawing (${form.paths.length} stroke${form.paths.length !== 1 ? 's' : ''})`}
                      type='cinnabar'
                      onPress={() => onChange({ paths: [] })}
                    />
                  </div>
                )}
              <div
                className='trialment_login_form_row'
                style={{ fontSize: '11px', opacity: 0.7, marginTop: '4px' }}
              >
                Use the pencil button next to the layer to enter draw mode on
                the board.
              </div>
            </>
          )}
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          {isEditing ? (
            <Button label='Delete' type='cinnabar' onPress={onDelete} />
          ) : (
            <span />
          )}
          <div
            style={{
              display: 'flex',
              gap: '10px',
              marginTop: '8px',
              marginBottom: '8px',
            }}
          >
            <Button
              label='Cancel'
              type={mode === 'dark' ? 'onyx' : 'black-white'}
              onPress={onCancel}
            />
            {massAddMode ? (
              <Button
                label={isMassUploading ? 'Adding Images…' : 'Add Images'}
                type='cinnabar'
                disabled={!massGroup.trim() || massFiles.length === 0 || isMassUploading}
                onPress={handleMassAdd}
              />
            ) : (
              <Button label='Save' type='cinnabar' onPress={onSave} />
            )}
          </div>
        </div>
      </div>
    </Rodal>
  );
}

export default LayerModal;
